import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { EnumSiNoTodos, ListSiNo, ListSiNoTodos, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { PeriodoCamItemViewModel } from 'src/app/components/_mantenimiento/campanias/periodos/datosbasicos/periodocam.types';
import { PeriodoDatosCaCamViewModel, CASelectedViewModel } from '../../../buscadores/solicitudes/buscador-solicitud-cam.types';
import { FilterSearchingRecintoUnidadInspeccion } from '../../../buscadores/unidades-inspeccion/buscador-unidad-inspeccion.types';
import {
    EnumDescripcionTcTipoSolicitudCamPlural,
    EnumDescripcionTcTipoSolicitudCamSingular,
    EnumIdsTcEstadoUnidadInspeccion,
    EnumIdsTcTipoSolicitudCam,
} from '../../../types/enum';
import { FormGroup, Validators } from '@angular/forms';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { combineLatest, map, Subject } from 'rxjs';
import { TwUnidadInspeccionViewModelExtended } from '../../unidad-inspeccion-datosbasicos.types';

@Component({
    selector: 'app-selector-unidad-inspeccion-filter',
    templateUrl: './selector-unidad-inspeccion-filter.component.html',
    styleUrls: ['./selector-unidad-inspeccion-filter.component.scss'],
})
export class SelectorUnidadInspeccionFilterComponent extends ComponentBase implements OnInit, OnChanges {
    @Input() editMode: boolean;
    @Input() unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    @Input() formGroup: FormGroup;
    @Input() lastFilter: FilterSearchingRecintoUnidadInspeccion;
    @Input() campanias: Tc[];
    @Input() periodos: PeriodoCamItemViewModel[];
    @Input() CAs: PeriodoDatosCaCamViewModel[];
    @Input() tiposSolicitud: Tc[];
    @Input() EAs: Tc[];
    @Input() operadores: Tc[];
    @Input() provincias: Tc[];
    @Input() municipios: Tc[];
    @Output() search = new EventEmitter<FilterSearchingRecintoUnidadInspeccion>();
    @Output() cleanForm = new EventEmitter<any>(); // Informa que el formulario se ha reseteado
    @Output() campaniaSelected = new EventEmitter<number>();
    @Output() periodoSelected = new EventEmitter<number>();
    @Output() caSelected = new EventEmitter<CASelectedViewModel>();
    @Output() provinciaSelected = new EventEmitter<number>();
    @Output() tipoSolicitudSelected = new EventEmitter<number>();

    @ViewChild('selectEstado') selectEstado: NgSelectComponent;

    isCollapsed = false;
    listSiNoTodos = ListSiNoTodos;
    listSiNo = ListSiNo;

    campaniaSeleccionada$: Subject<boolean>;
    periodoSeleccionado$: Subject<boolean>;
    tipoSolicitudSeleccionado$: Subject<boolean>;
    tamanioMaximoAgrupacion = true;

    constructor(
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.initListenersForOperadores();
        this.updateForm();
    }

    private initListenersForOperadores(): void {
        this.campaniaSeleccionada$ = new Subject();
        this.periodoSeleccionado$ = new Subject();
        this.tipoSolicitudSeleccionado$ = new Subject();
        combineLatest([this.campaniaSeleccionada$, this.periodoSeleccionado$, this.tipoSolicitudSeleccionado$])
            .pipe(
                map(([campaniaSelected, periodoSelected, tipoSolicitudSelected]) => {
                    const IdTipoSolicitud = this.form.controls.IdTipoSolicitudCam.value;
                    this.tipoSolicitudSelected.emit(IdTipoSolicitud);
                    return {};
                }),
            )
            .subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.form) {
            return;
        }
        if (changes?.lastFilter?.currentValue && this.lastFilter) {
            this.updateForm();
        }
        if (changes?.unidadInspeccion?.currentValue && this.unidadInspeccion) {
            this.patchForm();
        }
        if (changes?.CAs?.currentValue) {
            let IdCa = 0;
            if (this.isCA) {
                IdCa = this.loggedInUser?.TwUsuarioCa?.IdCa || 0;
            } else {
                IdCa = this.lastFilter?.IdCA || 0;
            }

            if (IdCa === null || IdCa === 0) {
                return;
            }
            const selectedCA: PeriodoDatosCaCamViewModel | undefined = this.CAs?.find(x => x.IdCa == IdCa);
            this.entidadAuditoraEnabledForCA = selectedCA?.EntidadAuditora || false;
        }
    }

    private updateForm(): void {
        this.formGroup.patchValue({
            IdCampania: this.lastFilter?.IdCampania || null,
            IdPeriodo: this.lastFilter?.IdPeriodo || null,
            IdCA: this.getIdCA(),
            IdEntidadAuditora: this.getIdEA(),
            CodigoUhc: this.lastFilter?.CodigoUhc || '',
            IdTipoSolicitudCam: this.lastFilter?.IdTipoSolicitudCam || null,
            IdEstadoUnidadInspeccion: this.lastFilter?.IdEstadoUnidadInspeccion || EnumIdsTcEstadoUnidadInspeccion.ID_SIN_ASIGNAR,
            IdOperador: this.lastFilter?.IdOperador || null,
            FechaDesdePresentacion: this.lastFilter?.FechaDesdePresentacion ? new Date(this.lastFilter?.FechaDesdePresentacion) : '',
            FechaHastaPresentacion: this.lastFilter?.FechaHastaPresentacion ? new Date(this.lastFilter?.FechaHastaPresentacion) : '',
            IdProvincia: this.lastFilter?.IdProvincia || null,
            IdMunicipio: this.lastFilter?.IdMunicipio || null,
            Agregado: this.lastFilter?.Agregado || null,
            Zona: this.lastFilter?.Zona || null,
            Poligono: this.lastFilter?.Poligono || null,
            Parcela: this.lastFilter?.Parcela || null,
            Recinto: this.lastFilter?.Recinto || null,
            // NOT VISIBLE
            Activa: this.lastFilter?.Activa || EnumSiNoTodos.TODOS, // NOT VISIBLE
            IdUsuario: this.lastFilter?.IdUsuario || null,
        });

        if (this.editMode) {
            this.formGroup.markAsDirty();
        }

        this.form = this.formGroup; // componentBase uses this.form
    }

    private patchForm(): void {
        if (this.editMode) {
            this.formGroup.patchValue({
                IdCampania: this.unidadInspeccion?.FirstRecinto?.IdCampania || null,
                IdPeriodo: this.unidadInspeccion?.FirstRecinto?.IdPeriodo || null,
                IdCA: this.unidadInspeccion?.FirstRecinto?.IdCa || null,
                IdEntidadAuditora: this.unidadInspeccion?.FirstRecinto?.IdEntidadAuditora || null,
                CodigoUhc: this.unidadInspeccion?.CodigoUhc || '',
                IdTipoSolicitudCam: this.unidadInspeccion?.FirstRecinto?.IdTipoSolicitudCam || null,
                IdEstadoUnidadInspeccion: this.unidadInspeccion?.IdEstadoUi || EnumIdsTcEstadoUnidadInspeccion.ID_SIN_ASIGNAR,
                IdOperador: this.unidadInspeccion?.FirstRecinto?.IdOperador || null,
            });

            const caSelectedVM = new CASelectedViewModel();
            caSelectedVM.idCA = this.unidadInspeccion?.FirstRecinto?.IdCa;
            caSelectedVM.idPeriodo = this.unidadInspeccion?.FirstRecinto?.IdPeriodo;

            this.caSelected.emit(caSelectedVM);
        }
    }

    /**
     * Manejador del evento para limpiar el formulario.
     * @param $event any Evento javascript
     */
    resetForm($event: any): void {
        $event.stopPropagation();

        if (!this.editMode) {
            // Si estamos en modo creación
            this.form.patchValue({
                IdCampania: null,
                IdPeriodo: null,
                IdCA: this.getIdCA(true),
                IdEntidadAuditora: this.getIdEA(true),
                CodigoUhc: '',
                IdTipoSolicitudCam: null,
                IdEstadoUnidadInspeccion: EnumIdsTcEstadoUnidadInspeccion.ID_SIN_ASIGNAR,
                IdOperador: null,
                // NOT VISIBLE
                Activa: EnumSiNoTodos.TODOS,
                IdUsuario: null, // Este campo no se rellena en el front. Se mantiene por compatibilidad de tipos con el core.
            });
            this.entidadAuditoraEnabledForCA = false;
            this.form.markAsPristine();
        }
        this.form.patchValue({
            FechaDesdePresentacion: null,
            FechaHastaPresentacion: null,
            IdProvincia: null,
            IdMunicipio: null,
            Poligono: null,
            Parcela: null,
            Recinto: null,
        });

        this.searchingButton = false;
        this.isCollapsed = false;

        this.cleanForm.emit();
    }

    /**
     * Manejador del evento de búsqueda.
     * En la búsqueda desde recoverLastSearch $event será nulo, por lo tanto no se resetean los valores asociados al paginador.
     * @param $event any Evento Javascript
     */
    onSearch($event: any): void {
        $event?.stopPropagation();
        this.searchingButton = true;
        this.isCollapsed = true;

        const payload: FilterSearchingRecintoUnidadInspeccion = {
            ...this.form.getRawValue(),
            PageNumber: this.lastFilter?.PageNumber || 0,
            Asc: this.lastFilter?.Asc || 0,
            OrderBy: this.lastFilter?.OrderBy || '',
        };

        this.search.emit(payload);
    }

    /**
     * Manejador que permite emitir el evento que indica que ha cambiado la Campaña.
     * @param idCampania number
     */
    onSelectCampania(idCampania: number): void {
        this.cleanDependentOnCampania();
        this.campaniaSeleccionada$.next(true);
        this.campaniaSelected.emit(idCampania);
    }

    /**
     * Este manejador permite emitir el evento que indica que el periodo ha sido seleccionado.
     * Este método también se invocará al recargar la consulta. Podrá venir a null, en ése caso se cargaría el listado completo de CAs.
     * @param idPeriodo number | null
     */
    onSelectPeriodo(idPeriodo: number | null): void {
        const periodo = this.periodos.find(periodo => periodo.Id === idPeriodo);
        this.tamanioMaximoAgrupacion = periodo?.TamanioMaximoAgrupacion === true;
        this.periodoSeleccionado$.next(true);
        this.periodoSelected.emit(idPeriodo!);
        this.cleanDependentOnPeriodo();
    }

    /**
     * Manejador del evento de selección de una CA en el desplegable.
     * Ignoramos el IdPeriodo que viene en el parámetro y lo tomamos del form.
     * @param $event PeriodoDatosCaCamViewModel | undefined
     */
    onSelectComunidadAutonoma(caSelected: PeriodoDatosCaCamViewModel | undefined): void {
        this.entidadAuditoraEnabledForCA = caSelected?.EntidadAuditora || false;
        this.setEntidadAuditora(this.getIdEA(true)!);
        this.setIdProvincia(null);
        this.setIdMunicipio(null);
        this.setIdTipoSolicitud(null);
        this.setIdOperador(null);

        const caSelectedVM = new CASelectedViewModel();
        caSelectedVM.idCA = caSelected?.IdCa;
        caSelectedVM.idPeriodo = this.form.controls.IdPeriodo.value;

        this.caSelected.emit(caSelectedVM);
    }

    private cleanDependentOnCampania(): void {
        this.setPeriodo(null);
        this.cleanDependentOnPeriodo();
    }

    private cleanDependentOnPeriodo(): void {
        this.entidadAuditoraEnabledForCA = false;
        this.setComunidadAutonoma(this.getIdCA(true)!);
        this.setEntidadAuditora(this.getIdEA(true)!);
        this.setIdTipoSolicitud(null);
        this.setIdOperador(null);
    }

    private setPeriodo(idPeriodo: number | null): void {
        this.form.patchValue({
            IdPeriodo: idPeriodo,
        });
        this.periodoSeleccionado$.next(true);
    }

    private setComunidadAutonoma(idCA: number | null): void {
        this.form.patchValue({
            IdCA: idCA,
        });
    }

    private setEntidadAuditora(idEntidadAuditora: number | null): void {
        this.form.patchValue({
            IdEntidadAuditora: idEntidadAuditora,
        });
    }

    /**
     * Manejador del evento de selección de una Provincia en el desplegable.
     * Ignoramos el valor que viene en el parámetro y lo tomamos del form.
     * @param $event Id y descripción de la provincia seleccionado
     */
    onSelectProvincia($event: any): void {
        const idProvincia = this.form.controls.IdProvincia.value;
        this.provinciaSelected.emit(idProvincia);
        this.setIdMunicipio(null);
    }

    /**
     * Manejador que permite emitir el evento que indica que ha cambiado el tipo de solicitud .
     * @param idtipoSolicitud number
     */
    onSelectTipoSolicitud(idtipoSolicitud: number): void {
        this.setIdOperador(null);
        this.tipoSolicitudSeleccionado$.next(idtipoSolicitud !== null);
    }

    onSelectEntidadAuditora(idEA: number): void {
        this.setIdOperador(null);
        this.tipoSolicitudSeleccionado$.next(true);
    }

    private setIdProvincia(idProvincia: number | null): void {
        this.form.patchValue({
            IdProvincia: idProvincia,
        });
    }

    private setIdMunicipio(idMunicipio: number | null): void {
        this.form.patchValue({
            IdMunicipio: idMunicipio,
        });
    }

    private setIdOperador(idOperador: number | null): void {
        this.form.patchValue({
            IdOperador: idOperador,
        });
    }

    private setIdTipoSolicitud(idtipoSolicitud: number | null): void {
        this.form.patchValue({
            IdTipoSolicitudCam: idtipoSolicitud,
        });
        this.tipoSolicitudSeleccionado$.next(idtipoSolicitud !== null);
    }

    /**
     * Esta función devuelve el Id de la comunidad autónoma si es CA.
     * Si no es CA, el que hubiera en la última petición o null si estamos en un reset.
     * @param reset boolean
     * @returns number|undefined
     */
    private getIdCA(reset = false): number | undefined {
        if (this.isCA && this.loggedInUser?.TwUsuarioCa) {
            return this.loggedInUser?.TwUsuarioCa.IdCa;
        } else if (reset) {
            return undefined;
        } else if (this.lastFilter) {
            return this.lastFilter.IdCA || undefined;
        }
        return undefined;
    }

    /**
     * Esta función devuelve el Id de la entidad auditora si es EA.
     * Si no es EA, el que hubiera en la última petición o null si estamos en un reset.
     * @param reset boolean
     * @returns number|undefined
     */
    private getIdEA(reset = false): number | undefined {
        if (this.isEA && this.loggedInUser?.TwRegTecnicoEntidadAuditora) {
            return this.loggedInUser?.TwRegTecnicoEntidadAuditora.IdRegEntidadAuditora;
        } else if (reset) {
            return undefined;
        } else if (this.lastFilter) {
            return this.lastFilter.IdEntidadAuditora || undefined;
        }
        return undefined;
    }

    get EANoHabilitada(): boolean {
        return (
            this.CAs?.length > 0 &&
            this.form.controls.IdCampania.value !== null &&
            this.form.controls.IdCampania.value !== undefined &&
            this.form.controls.IdCampania.value !== 0 &&
            this.form.controls.IdPeriodo.value !== null &&
            this.form.controls.IdPeriodo.value !== undefined &&
            this.form.controls.IdPeriodo.value !== 0 &&
            this.form.controls.IdCA.value !== null &&
            this.form.controls.IdCA.value !== undefined &&
            this.form.controls.IdCA.value !== 0 &&
            !this.entidadAuditoraEnabledForCA
        );
    }

    private EAEnabledForCA = false;

    get entidadAuditoraEnabledForCA(): boolean {
        return this.EAEnabledForCA;
    }

    set entidadAuditoraEnabledForCA($val: boolean) {
        this.EAEnabledForCA = $val;
        this.manageEAValidation();
    }

    private manageEAValidation(): void {
        if (this.entidadAuditoraEnabledForCA) {
            this.formGroup.controls['IdEntidadAuditora'].addValidators(Validators.required);
        } else {
            this.formGroup.controls['IdEntidadAuditora'].removeValidators(Validators.required);
        }
        this.formGroup.controls['IdEntidadAuditora'].updateValueAndValidity();
    }

    private getIdTipoSolicitudCam(): number | undefined {
        let idTipoSolicitudCam = undefined;
        if (this.unidadInspeccion?.FirstRecinto) {
            idTipoSolicitudCam = this.unidadInspeccion.FirstRecinto?.IdTipoSolicitudCam;
        } else {
            idTipoSolicitudCam = this.formGroup.controls['IdTipoSolicitudCam'].value;
        }

        return idTipoSolicitudCam;
    }

    get title(): any {
        const idTipoSolicitudCam = this.getIdTipoSolicitudCam();
        switch (idTipoSolicitudCam) {
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.PARCELA,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.PARCELA,
                };
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.INVERNADERO,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.INVERNADERO,
                };
            default:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };
        }
    }

    /**
     * Para controlar la propiedad disabled del botón de búsqueda.
     * Mientras no estén cargadas las campanias, no se habilita el botón.
     * @returns boolean
     */
    get disableSearchBtn(): boolean {
        return this.isCollapsed || !this.campanias || !this.tiposSolicitud || this.formInvalid || this.esCampaniaDisabled;
    }

    /**
     * Solo tenemos en cuenta si el desplegable campaña está deshabilitado en la creación, no en la edición,
     * pues en la edición todos éstos campos iniciales están deshabiltados.
     */
    get esCampaniaDisabled(): boolean {
        return this.formGroup.controls['IdCampania'].disabled && !this.editMode;
    }

    /**
     * Para controlar la propiedad disabled del botón de reset.
     * @returns boolean
     */
    get formIsUntouched(): boolean {
        return this.form.pristine || !this.form.dirty;
    }

    private get formInvalid(): boolean {
        return this.formGroup.invalid || this.formIsUntouched;
    }
}
